import { useState, useContext, useEffect } from 'react'
import { useSearchParams } from 'next/navigation'
import useSWR from 'swr'
import { arcadeApiClient } from 'apiClients'
import { AppContext } from 'providers'
import { Card } from 'types'

const guideFetcher = (url: string) =>
  arcadeApiClient.get(url, {}).then(res => res.data.guide)

const cardsFetcher = (url: string) =>
  arcadeApiClient.get(url, {}).then(res => res.data.cards)

export const useGuide = () => {
  const { setToast } = useContext(AppContext)
  const [cardIndex, setCardIndex] = useState(0)
  const [canGoNext, setCanGoNext] = useState(true)
  const [cards, setCards] = useState<Card[]>([])
  const guideId = useSearchParams().get('id')

  const {
    data: guide,
    isValidating: guideLoading,
    error: guideError,
  } = useSWR(`/multi_platform/guides/${guideId}`, guideFetcher)

  const {
    data: initialCards,
    isValidating: cardsLoading,
    error: cardsError,
  } = useSWR(`/multi_platform/guides/${guideId}/cards`, cardsFetcher)

  useEffect(() => {
    if (initialCards) {
      const completedCard = {
        id: -1,
        format: 'completed',
      }
      initialCards.push(completedCard)
      setCards(initialCards)
    }
  }, [initialCards])

  useEffect(() => {
    if (!initialCards || cardsLoading) return

    setCanGoNext(cardIndex < cards.length - 1)

    // TODO: investigate why is it possible for currentCard to be undefined
    const currentCard = cards[cardIndex] as Card | undefined

    // If the current card is a question and it hasn't been answered, disable the "next" button
    if (currentCard) {
      if (currentCard?.question && !currentCard.question.answered) {
        setCanGoNext(false)
      } else if (['video', 'button'].includes(currentCard?.format)) {
        setCanGoNext(false)
      }
    }

    if (cardIndex === cards.length - 1 && guide.userStatus !== 'completed') {
      completeGuide()
    }
  }, [cardIndex, cards.length])

  const nextPage = () => setCardIndex(Math.min(cardIndex + 1, cards.length - 1))
  const prevPage = () => setCardIndex(Math.max(0, cardIndex - 1))
  const startOver = () => setCardIndex(0)

  const answerQuestion = async (questionId: number, optionId: number) => {
    try {
      const response = await arcadeApiClient.post(`/multi_platform/answers`, {
        question_id: questionId,
        option_id: optionId,
      })
      const updatedCardData = response.data.card

      const updatedCards = cards.map(card =>
        card.id === updatedCardData.id ? updatedCardData : card,
      )
      setCards(updatedCards)
    } catch (error) {
      setToast({ description: 'Error submitting answer', status: 'error' })
    } finally {
      setCanGoNext(true)
    }
  }

  const onLinkClick = () => {
    arcadeApiClient.post(
      `/multi_platform/guides/${guideId}/cards/${cards[cardIndex].id}/link_clicked`,
    )
  }

  const completeGuide = () =>
    arcadeApiClient
      .put(`/multi_platform/guides/${guideId}/complete`)
      .catch(() =>
        setToast({ description: 'Error completing guide', status: 'error' }),
      )

  const canGoPrev = cardIndex > 0
  const shouldShowPrev = cardIndex > 0
  const shouldShowNext = cardIndex < cards?.length - 1

  return {
    answerQuestion,
    canGoNext,
    canGoPrev,
    cardIndex,
    cards,
    cardsError,
    cardsLoading,
    guide,
    guideError,
    guideId,
    guideLoading,
    nextPage,
    onLinkClick,
    prevPage,
    progress: cards ? ((cardIndex + 1) / cards.length) * 100 : 0,
    setCanGoNext,
    shouldShowNext,
    shouldShowPrev,
    startOver,
  }
}
